import { Injectable } from '@angular/core';
import { Subscription } from "rxjs";
import { MidiPlayerService } from "./midi-player.service";
import { ScrollContainerService } from './scroll-container.service';

@Injectable({
  providedIn: 'root'
})
export class AutoScrollService {

  private autoScrollSubscription: Subscription | null = null;
  private animateFrameRef: number | null = null;
  private readonly CONTAINER_ID = 'hymn-scroll-container';

  enabled = false;
  active = false;
  mode: 'off' | 'autoscroll' | 'karaoke' = 'off';

  // NEW: Configurable boolean for linear scrolling
  public useLinearScrolling = true;

  // When true, autoscroll runs regardless of the user setting (used by continuous playback)
  private forced = false;

  constructor(private midiPlayerService: MidiPlayerService,
              private scrollContainerService: ScrollContainerService) { }

  startAutoScroll(): void {
    this.enabled = true;
    this.active = true;

    if (this.autoScrollSubscription) {
      this.autoScrollSubscription.unsubscribe();
    }

    // Only subscribe to progress for autoscroll mode, not karaoke mode
    if (this.mode === 'autoscroll') {
      this.autoScrollSubscription = this.midiPlayerService.progress$.subscribe((progress) => {
        if (this.active && (this.enabled || this.forced)) {
          // Only scroll if we aren't in the intro and MIDI is actually playing
          if (!this.midiPlayerService.introPlaying && this.midiPlayerService.playing && progress >= 0 && progress <= 100) {
            this.scrollToPercentage(progress, 1200);
          } else if (this.midiPlayerService.introPlaying) {
            this.scrollToPercentage(0, 100);
          }
        }
      });
    }
  }

  // Added 'instant' flag so we can snap to the top in 0ms when needed
  scrollToTop(instant: boolean = false): void {
    this.cancelRunningAnimation();
    if (instant) {
      const container = this.resolveScrollContainer();
      if (container) container.scrollTop = 0;
    } else {
      this.scrollToPercentage(0, 100);
    }
  }

  setMode(mode: 'off' | 'autoscroll' | 'karaoke'): void {
    this.mode = mode;
    this.enabled = mode !== 'off';
  }

  enableAutoScroll(): void {
    this.enabled = true;
  }

  disableAutoScroll(): void {
    this.enabled = false;
    if (!this.forced) {
      this.stopAutoScroll();
    }
  }

  forceOn(): void {
    this.forced = true;
    this.startAutoScroll();
  }

  forceOff(): void {
    this.forced = false;
  }

  stopAutoScroll(scrollTop: boolean = false): void {
    this.active = false;

    if (this.autoScrollSubscription) {
      this.autoScrollSubscription.unsubscribe();
      this.autoScrollSubscription = null;
    }

    this.cancelRunningAnimation();

    if (scrollTop) {
      const container = this.resolveScrollContainer();
      if (container) container.scrollTop = 0;
    }
  }

  /**
   * Cleans up the existing animation frame to prevent multiple
   * scroll loops from fighting each other.
   * Made public so components can instantly kill running scroll animations!
   */
  cancelRunningAnimation(): void {
    if (this.animateFrameRef !== null) {
      cancelAnimationFrame(this.animateFrameRef);
      this.animateFrameRef = null;
    }
  }

  /**
   * Resolve the correct scroll container. Prefer a specifically scrollable ancestor
   * of the legacy #hymn-scroll-container; fall back to document.scrollingElement.
   */
  private resolveScrollContainer(): HTMLElement | null {
    // 0) Prefer an explicitly registered container via the service
    const registered = this.scrollContainerService.getActive();
    if (registered) return registered;

    const legacy = document.getElementById(this.CONTAINER_ID);
    const isScrollable = (el: Element | null) => !!el && (el as HTMLElement).scrollHeight > (el as HTMLElement).clientHeight;

    // 1) If the legacy element itself scrolls, use it
    if (isScrollable(legacy)) return legacy as HTMLElement;

    // 2) Walk up to find the nearest scrollable ancestor
    let node: Element | null = legacy || null;
    while (node && node.parentElement) {
      node = node.parentElement;
      if (isScrollable(node)) return node as HTMLElement;
      const style = getComputedStyle(node);
      const overflowY = style.overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && (node as HTMLElement).clientHeight > 0) {
        return node as HTMLElement;
      }
    }

    // 3) Fall back to document scrolling element
    const docEl = document.scrollingElement as HTMLElement | null;
    if (docEl && isScrollable(docEl)) return docEl;
    return docEl || document.documentElement || document.body as HTMLElement;
  }

  scrollToPercentage(percentage: number, duration: number): void {
    const container = this.resolveScrollContainer();
    if (!container) return;

    // IMPORTANT: Kill the previous animation loop before starting a new one
    this.cancelRunningAnimation();

    const totalHeight = Math.max(0, container.scrollHeight - container.clientHeight);
    if (totalHeight <= 0) return; // nothing to scroll

    // Slight adjustment so we don't lag behind on very tall content
    const scrollAdjustment = 1 + 0.25 * (container.clientHeight / Math.max(container.scrollHeight, 1));
    const targetPosition = (percentage / 100) * totalHeight * scrollAdjustment;

    const startPosition = container.scrollTop;
    const distance = targetPosition - startPosition;

    let startTime: number | null = null;

    const animateScroll = (currentTime: number) => {
      if (startTime == null) {
        startTime = currentTime;
      }

      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);

      // Apply the scroll position (always linear for percentage scroll)
      container.scrollTop = startPosition + distance * progress;

      if (timeElapsed < duration && this.active && (this.enabled || this.forced)) {
        this.animateFrameRef = requestAnimationFrame(animateScroll);
      } else {
        this.animateFrameRef = null;
      }
    };

    this.animateFrameRef = requestAnimationFrame(animateScroll);
  }

  scrollToElement(element: HTMLElement, duration: number = 800): void {
    const container = this.resolveScrollContainer();
    if (!container) return;

    // IMPORTANT: Kill the previous animation loop before starting a new one
    this.cancelRunningAnimation();

    // Calculate the position to place the element at ~27% from top (optimal for karaoke reading)
    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const elementTop = elementRect.top - containerRect.top + container.scrollTop;
    const targetPosition = elementTop - (container.clientHeight * 0.27);

    const startPosition = container.scrollTop;
    const distance = targetPosition - startPosition;

    let startTime: number | null = null;

    const animateScroll = (currentTime: number) => {
      if (startTime == null) {
        startTime = currentTime;
      }

      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);

      // Check the boolean to determine which easing to use
      const currentProgress = this.useLinearScrolling
        ? progress
        : 1 - Math.pow(1 - progress, 3); // Ease-out effect

      // Apply the scroll position
      container.scrollTop = startPosition + distance * currentProgress;

      if (timeElapsed < duration && this.active && (this.enabled || this.forced)) {
        this.animateFrameRef = requestAnimationFrame(animateScroll);
      } else {
        this.animateFrameRef = null;
      }
    };

    this.animateFrameRef = requestAnimationFrame(animateScroll);
  }
}
